import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { VerifiedBadge, channelName, formatNum } from './ArticleBits';

interface Props {
  article: Article;
  /** layout='list' 横向图文；layout='card' 竖向卡片 */
  layout?: 'list' | 'card';
  showExcerpt?: boolean;
}

export default function ArticleCard({ article, layout = 'list', showExcerpt = true }: Props) {
  if (layout === 'card') {
    return (
      <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
        <Link to={`/article/${article.id}`} className="block aspect-[16/10] overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <div className="flex items-center gap-2">
            <span className="rounded-sm bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
              {channelName[article.channel]}
            </span>
            <VerifiedBadge status={article.verified} />
          </div>
          <Link to={`/article/${article.id}`} className="mt-3">
            <h3 className="line-clamp-2 font-display text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
              {article.title}
            </h3>
          </Link>
          {showExcerpt && (
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{article.excerpt}</p>
          )}
          <div className="mt-auto flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatNum(article.views)}
            </span>
            <span className="flex items-center gap-1 whitespace-nowrap">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {formatNum(article.comments)}
            </span>
            <span className="whitespace-nowrap text-foreground-400">{article.date}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex gap-4 rounded-lg p-2 transition-colors hover:bg-background-100 md:gap-5">
      <div className="shrink-0 overflow-hidden rounded-md bg-background-200">
        <Link to={`/article/${article.id}`} className="block h-24 w-28 sm:h-28 sm:w-40 md:h-32 md:w-52">
          <img
            src={article.cover}
            alt={article.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-primary-700">{channelName[article.channel]}</span>
          <span className="h-3 w-px bg-background-300" aria-hidden="true"></span>
          <VerifiedBadge status={article.verified} />
        </div>
        <Link to={`/article/${article.id}`} className="mt-1.5">
          <h3 className="line-clamp-2 font-display text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            {article.title}
          </h3>
        </Link>
        {showExcerpt && (
          <p className="mt-1.5 hidden line-clamp-2 text-sm leading-6 text-foreground-600 sm:block md:line-clamp-1">
            {article.excerpt}
          </p>
        )}
        <div className="mt-auto flex items-center gap-3 pt-1.5 text-xs text-foreground-500">
          <span className="whitespace-nowrap">{article.author}</span>
          <span className="hidden whitespace-nowrap text-foreground-400 sm:inline">{article.role}</span>
          <span className="ml-auto flex items-center gap-2 whitespace-nowrap">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatNum(article.views)}
            </span>
            <span className="hidden items-center gap-1 sm:flex">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {formatNum(article.comments)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}